// Simple translation system for the chatapp
// This provides basic translations for the navigation and common UI elements

export type TranslationKey = keyof typeof translations.fr;

export const translations = {
  fr: {
    // Navigation
    'navigation.dashboard': 'Tableau de bord',
    'navigation.customers': 'Clients',
    'navigation.pipeline': 'Pipeline',
    'navigation.tasks': 'Tâches',
    'navigation.reports': 'Rapports',
    'navigation.userManagement': 'Gestion des utilisateurs',
    'navigation.planning': 'Planning',
    'navigation.toProcess': 'À traiter',
    'navigation.preferences': 'Préférences',
    'navigation.myExpertises': 'Mes expertises',
    'navigation.newRequest': 'Nouvelle demande',
    'navigation.documents': 'Documents',
    'navigation.statistics': 'Statistiques',
    'navigation.logout': 'Déconnexion',
    
    // Common
    'common.search': 'Rechercher',
    'common.filter': 'Filtrer',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.add': 'Ajouter',
    'common.close': 'Fermer',
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.warning': 'Attention',
    'common.info': 'Information',
    'common.total': 'Total',
    'common.page': 'Page',
    'common.all': 'Tous',
    'common.paid': 'Payé',
    'common.unpaid': 'Non payé',
    'common.noResults': 'Aucun résultat trouvé',
    'common.modifySearch': 'Essayez de modifier votre recherche ou vos filtres.',
    'common.copyToClipboard': 'Copier',
    'common.copied': 'Copié!',
    
    // Banque Reports
    'banque.reports.title': 'Rapports Financiers',
    'banque.reports.subtitle': 'Consultez les rapports et statistiques financières',
    'banque.reports.clientId': 'Client ID',
    'banque.reports.report': 'rapport',
    'banque.reports.reports': 'rapports',
    'banque.reports.noReports': 'Aucun rapport disponible',
    'banque.reports.noReportsMessage': 'Aucun rapport disponible pour le moment',
    'banque.reports.noMatchingReports': 'Aucun rapport ne correspond aux critères de recherche',
    'banque.reports.searchPlaceholder': 'Rechercher par client, expert, numéro...',
    'banque.reports.sortBy.progress': 'Progression',
    'banque.reports.sortBy.date': 'Date',
    'banque.reports.sortBy.amount': 'Montant',
    'banque.reports.sortBy.id': 'Numéro',
    'banque.reports.missingConfig': 'Configuration manquante',
    'banque.reports.missingConfigMessage': 'Votre compte n\'est pas associé à un identifiant client. Veuillez contacter l\'administrateur.',
    'banque.reports.loadingReports': 'Chargement des rapports...',
    'banque.reports.clientNotSpecified': 'Client non renseigné',
    'banque.reports.expertNotAssigned': 'Non assigné',
    'banque.reports.financialReport': 'Rapport financier',
    'banque.reports.france': 'France',
    'banque.reports.partial': 'Paiement partiel',
    'banque.reports.refunded': 'Remboursé',
    'banque.reports.invoiceIssued': 'Facture émise',
    
    // Expertise List
    'expertise.list.noExpertiseFound': 'Aucune expertise trouvée',
    'expertise.list.noExpertisesToDisplay': 'Il n\'y a pas d\'expertises à afficher.',
    'expertise.list.noResultsFound': 'Aucun résultat trouvé',
    'expertise.list.modifySearchOrFilters': 'Essayez de modifier votre recherche ou vos filtres.',
    
    // Expertise Card
    'expertise.card.realEstateExpertise': 'Expertise immobilière',
    'expertise.card.paid': 'Payé',
    'expertise.card.invoiceIssued': 'Facture émise',
    'expertise.card.assignedExpert': 'Expert assigné',
    'expertise.card.progress': 'Progression',
    'expertise.card.detail': 'Détail',
    'expertise.card.fileMaker': 'FM',
    'expertise.card.createTask': 'Tâche',
    'expertise.card.message': 'Tâches',
    'expertise.card.notAvailable': 'N/A',
    
    // Stage markers
    'expertise.stages.officeToEncode': 'Bureau à encoder',
    'expertise.stages.officeToPlan': 'Bureau à planifier',
    'expertise.stages.appointmentPending': 'RV en attente',
    'expertise.stages.expertToVisit': 'Expert à visiter',
    'expertise.stages.expertToComplete': 'Expert à achever',
    'expertise.stages.officeToValuate': 'Bureau à valoriser',
    'expertise.stages.officeToFinish': 'Bureau à achever',
    'expertise.stages.waitingDocsInfo': 'Attente doc/infos',
    'expertise.stages.waitingPayment': 'Attente paiement',
    'expertise.stages.waitingConfirmation': 'Attente confirmation',
    'expertise.stages.toTransmit': 'À transmettre',
  },
  en: {
    // Navigation
    'navigation.dashboard': 'Dashboard',
    'navigation.customers': 'Customers',
    'navigation.pipeline': 'Pipeline',
    'navigation.tasks': 'Tasks',
    'navigation.reports': 'Reports',
    'navigation.userManagement': 'User Management',
    'navigation.planning': 'Planning',
    'navigation.toProcess': 'To Process',
    'navigation.preferences': 'Preferences',
    'navigation.myExpertises': 'My Expertises',
    'navigation.newRequest': 'New Request',
    'navigation.documents': 'Documents',
    'navigation.statistics': 'Statistics',
    'navigation.logout': 'Logout',
    
    // Common
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.close': 'Close',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.warning': 'Warning',
    'common.info': 'Information',
    'common.total': 'Total',
    'common.page': 'Page',
    'common.all': 'All',
    'common.paid': 'Paid',
    'common.unpaid': 'Unpaid',
    'common.noResults': 'No results found',
    'common.modifySearch': 'Try modifying your search or filters.',
    'common.copyToClipboard': 'Copy',
    'common.copied': 'Copied!',

    // Banque Reports
    'banque.reports.title': 'Financial Reports',
    'banque.reports.subtitle': 'View financial reports and statistics',
    'banque.reports.clientId': 'Client ID',
    'banque.reports.report': 'report',
    'banque.reports.reports': 'reports',
    'banque.reports.noReports': 'No reports available',
    'banque.reports.noReportsMessage': 'No reports available at the moment',
    'banque.reports.noMatchingReports': 'No reports match the search criteria',
    'banque.reports.searchPlaceholder': 'Search by client, expert, number...',
    'banque.reports.sortBy.progress': 'Progress',
    'banque.reports.sortBy.date': 'Date',
    'banque.reports.sortBy.amount': 'Amount',
    'banque.reports.sortBy.id': 'Number',
    'banque.reports.missingConfig': 'Missing configuration',
    'banque.reports.missingConfigMessage': 'Your account is not associated with a client ID. Please contact the administrator.',
    'banque.reports.loadingReports': 'Loading reports...',
    'banque.reports.clientNotSpecified': 'Client not specified',
    'banque.reports.expertNotAssigned': 'Not assigned',
    'banque.reports.financialReport': 'Financial report',
    'banque.reports.france': 'France',
    'banque.reports.partial': 'Partial payment',
    'banque.reports.refunded': 'Refunded',
    'banque.reports.invoiceIssued': 'Invoice issued',
    
    // Expertise List
    'expertise.list.noExpertiseFound': 'No expertise found',
    'expertise.list.noExpertisesToDisplay': 'There are no expertises to display.',
    'expertise.list.noResultsFound': 'No results found',
    'expertise.list.modifySearchOrFilters': 'Try modifying your search or filters.',
    
    // Expertise Card
    'expertise.card.realEstateExpertise': 'Real estate expertise',
    'expertise.card.paid': 'Paid',
    'expertise.card.invoiceIssued': 'Invoice issued',
    'expertise.card.assignedExpert': 'Assigned expert',
    'expertise.card.progress': 'Progress',
    'expertise.card.detail': 'Detail',
    'expertise.card.fileMaker': 'FM',
    'expertise.card.createTask': 'Task',
    'expertise.card.message': 'Tasks',
    'expertise.card.notAvailable': 'N/A',
    
    // Stage markers
    'expertise.stages.officeToEncode': 'Office to encode',
    'expertise.stages.officeToPlan': 'Office to plan',
    'expertise.stages.appointmentPending': 'Appointment pending',
    'expertise.stages.expertToVisit': 'Expert to visit',
    'expertise.stages.expertToComplete': 'Expert to complete',
    'expertise.stages.officeToValuate': 'Office to valuate',
    'expertise.stages.officeToFinish': 'Office to finish',
    'expertise.stages.waitingDocsInfo': 'Waiting docs/info',
    'expertise.stages.waitingPayment': 'Waiting payment',
    'expertise.stages.waitingConfirmation': 'Waiting confirmation',
    'expertise.stages.toTransmit': 'To transmit',
  },
  nl: {
    // Navigation
    'navigation.dashboard': 'Dashboard',
    'navigation.customers': 'Klanten',
    'navigation.pipeline': 'Pipeline',
    'navigation.tasks': 'Taken',
    'navigation.reports': 'Rapporten',
    'navigation.userManagement': 'Gebruikersbeheer',
    'navigation.planning': 'Planning',
    'navigation.toProcess': 'Te verwerken',
    'navigation.preferences': 'Voorkeuren',
    'navigation.myExpertises': 'Mijn expertises',
    'navigation.newRequest': 'Nieuwe aanvraag',
    'navigation.documents': 'Documenten',
    'navigation.statistics': 'Statistieken',
    'navigation.logout': 'Uitloggen',
    
    // Common
    'common.search': 'Zoeken',
    'common.filter': 'Filteren',
    'common.save': 'Opslaan',
    'common.cancel': 'Annuleren',
    'common.delete': 'Verwijderen',
    'common.edit': 'Bewerken',
    'common.add': 'Toevoegen',
    'common.close': 'Sluiten',
    'common.loading': 'Laden...',
    'common.error': 'Fout',
    'common.success': 'Succes',
    'common.warning': 'Waarschuwing',
    'common.info': 'Informatie',
    'common.total': 'Totaal',
    'common.page': 'Pagina',
    'common.all': 'Alle',
    'common.paid': 'Betaald',
    'common.unpaid': 'Onbetaald',
    'common.noResults': 'Geen resultaten gevonden',
    'common.modifySearch': 'Probeer uw zoekopdracht of filters aan te passen.',
    'common.copyToClipboard': 'Kopiëren',
    'common.copied': 'Gekopieerd!',

    // Banque Reports
    'banque.reports.title': 'Financiële Rapporten',
    'banque.reports.subtitle': 'Bekijk financiële rapporten en statistieken',
    'banque.reports.clientId': 'Klant ID',
    'banque.reports.report': 'rapport',
    'banque.reports.reports': 'rapporten',
    'banque.reports.noReports': 'Geen rapporten beschikbaar',
    'banque.reports.noReportsMessage': 'Er zijn momenteel geen rapporten beschikbaar',
    'banque.reports.noMatchingReports': 'Geen rapporten voldoen aan de zoekcriteria',
    'banque.reports.searchPlaceholder': 'Zoek op klant, expert, nummer...',
    'banque.reports.sortBy.progress': 'Voortgang',
    'banque.reports.sortBy.date': 'Datum',
    'banque.reports.sortBy.amount': 'Bedrag',
    'banque.reports.sortBy.id': 'Nummer',
    'banque.reports.missingConfig': 'Configuratie ontbreekt',
    'banque.reports.missingConfigMessage': 'Uw account is niet gekoppeld aan een klant-ID. Neem contact op met de beheerder.',
    'banque.reports.loadingReports': 'Rapporten laden...',
    'banque.reports.clientNotSpecified': 'Klant niet opgegeven',
    'banque.reports.expertNotAssigned': 'Niet toegewezen',
    'banque.reports.financialReport': 'Financieel rapport',
    'banque.reports.france': 'Frankrijk',
    'banque.reports.partial': 'Gedeeltelijke betaling',
    'banque.reports.refunded': 'Terugbetaald',
    'banque.reports.invoiceIssued': 'Factuur uitgegeven',
    
    // Expertise List
    'expertise.list.noExpertiseFound': 'Geen expertise gevonden',
    'expertise.list.noExpertisesToDisplay': 'Er zijn geen expertises om weer te geven.',
    'expertise.list.noResultsFound': 'Geen resultaten gevonden',
    'expertise.list.modifySearchOrFilters': 'Probeer uw zoekopdracht of filters aan te passen.',
    
    // Expertise Card
    'expertise.card.realEstateExpertise': 'Vastgoedexpertise',
    'expertise.card.paid': 'Betaald',
    'expertise.card.invoiceIssued': 'Factuur uitgegeven',
    'expertise.card.assignedExpert': 'Toegewezen expert',
    'expertise.card.progress': 'Voortgang',
    'expertise.card.detail': 'Detail',
    'expertise.card.fileMaker': 'FM',
    'expertise.card.createTask': 'Taak',
    'expertise.card.message': 'Taken',
    'expertise.card.notAvailable': 'N.v.t.',
    
    // Stage markers
    'expertise.stages.officeToEncode': 'Bureau om te coderen',
    'expertise.stages.officeToPlan': 'Bureau om te plannen',
    'expertise.stages.appointmentPending': 'Afspraak in afwachting',
    'expertise.stages.expertToVisit': 'Expert om te bezoeken',
    'expertise.stages.expertToComplete': 'Expert om te voltooien',
    'expertise.stages.officeToValuate': 'Bureau om te waarderen',
    'expertise.stages.officeToFinish': 'Bureau om af te ronden',
    'expertise.stages.waitingDocsInfo': 'Wachten op doc/info',
    'expertise.stages.waitingPayment': 'Wachten op betaling',
    'expertise.stages.waitingConfirmation': 'Wachten op bevestiging',
    'expertise.stages.toTransmit': 'Om door te geven',
  },
  de: {
    // Navigation
    'navigation.dashboard': 'Dashboard',
    'navigation.customers': 'Kunden',
    'navigation.pipeline': 'Pipeline',
    'navigation.tasks': 'Aufgaben',
    'navigation.reports': 'Berichte',
    'navigation.userManagement': 'Benutzerverwaltung',
    'navigation.planning': 'Planung',
    'navigation.toProcess': 'Zu bearbeiten',
    'navigation.preferences': 'Einstellungen',
    'navigation.myExpertises': 'Meine Expertisen',
    'navigation.newRequest': 'Neue Anfrage',
    'navigation.documents': 'Dokumente',
    'navigation.statistics': 'Statistiken',
    'navigation.logout': 'Abmelden',
    
    // Common
    'common.search': 'Suchen',
    'common.filter': 'Filtern',
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.delete': 'Löschen',
    'common.edit': 'Bearbeiten',
    'common.add': 'Hinzufügen',
    'common.close': 'Schließen',
    'common.loading': 'Laden...',
    'common.error': 'Fehler',
    'common.success': 'Erfolg',
    'common.warning': 'Warnung',
    'common.info': 'Information',
    'common.copyToClipboard': 'Kopieren',
    'common.copied': 'Kopiert!',
  },
};